import { ApiError } from "@/lib/api";
import type { AuthedRequester, AuthedUploader } from "@/lib/brokerAuth";
import type { AccountDocument } from "@/lib/brokerAccounts";
import { readRules, type DocumentRules } from "@/lib/brokerDocumentTypes";

// BRK-S330 — the Signal Advance application's EVIDENCE, consuming PMS-S722.
//
// Decided 2026-10-07 (Aaron, plan rule 13): the application has two
// requirements, BANK and ACCOUNTING, and each is satisfied INDEPENDENTLY by its
// link (Plaid / Rutter) OR by uploaded documents. A contractor may link Plaid and
// upload financials, or link Rutter and upload bank statements.
//
// PMS OWNS THE RULE. Which state a requirement is in, and whether the whole
// application is satisfied, is PMS's derivation (`advanceRequirements` on the
// broker account read); this module reads it and nothing here re-derives it from
// the documents on file. The accepted document types are PMS's too — served per
// requirement by `GET /api/broker/document-types?requirement=` and never
// hardcoded here (the ids are mirrored for LABELS only, in
// `brokerDocumentTypes.ts`).
//
// ── PINNED AT THE SPRINT-10 RELEASE RE-PIN ──────────────────────────────────
//
// The block, the `?requirement=` query and the two account-document writes
// below were written by hand to the shapes agreed with the PMS-S722 agent
// (2026-10-07) and read ahead of the pin; the sprint-10 release re-pin
// publishes all of them, as written.
//
// ── THE TOLERANT MIRROR (the BRK-S114 lesson) ───────────────────────────────
//
// An ABSENT block is a PMS that predates PMS-S722, or an account with no
// application: the section renders nothing. A state this build has not adopted
// renders a neutral "Status unavailable" chip and offers nothing — an unknown
// state is never actionable.

/** The two requirements, in the order the section shows them. */
export const ADVANCE_EVIDENCE_REQUIREMENTS = ["bank", "accounting"] as const;
export type AdvanceEvidenceRequirement =
  (typeof ADVANCE_EVIDENCE_REQUIREMENTS)[number];

/**
 * One requirement's state, mirrored closed from PMS-S722. A met requirement is
 * `satisfied` whichever way it was met (`via` says how); while unmet the state
 * follows the link (not_started → link_sent → connected). `documents_received`
 * is in the vocabulary but never emitted in the MVP — mirrored, and neutral.
 */
export const ADVANCE_EVIDENCE_STATES = [
  "not_started",
  "link_sent",
  "connected",
  "documents_received",
  "satisfied",
] as const;
export type AdvanceEvidenceState = (typeof ADVANCE_EVIDENCE_STATES)[number];

/** How a requirement was (or is being) met. `null` until anything happened. */
export type AdvanceEvidenceVia = "link" | "documents" | null;

/** One requirement on the wire. Its client link's facts are the connections
 *  read's (`listAccountConnections`, BRK-S446), not this block's. */
export interface AdvanceRequirementWire {
  state?: string;
  via?: string | null;
}

/** The wire block — OPEN strings, narrowed by `readAdvanceRequirements`. */
export interface AdvanceRequirementsWire {
  bank?: AdvanceRequirementWire | null;
  accounting?: AdvanceRequirementWire | null;
  satisfied?: boolean;
}

/** One requirement, as the card renders it. */
export interface AdvanceEvidence {
  /** `null` for a state this build has not adopted — never actionable. */
  state: AdvanceEvidenceState | null;
  via: AdvanceEvidenceVia;
}

export interface ReadAdvanceRequirements {
  bank: AdvanceEvidence;
  accounting: AdvanceEvidence;
  /** Both requirements met: the application is with the underwriter. */
  satisfied: boolean;
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readEvidence(raw: unknown): AdvanceEvidence | null {
  const row = record(raw);
  if (row === null) return null;
  const state = (ADVANCE_EVIDENCE_STATES as readonly unknown[]).includes(
    row.state,
  )
    ? (row.state as AdvanceEvidenceState)
    : null;
  const via =
    row.via === "link" || row.via === "documents"
      ? (row.via as "link" | "documents")
      : null;
  return { state, via };
}

/**
 * `advanceRequirements`, read — or `null` when PMS sent no block (absent, null,
 * or neither requirement is a record), which renders NOTHING.
 *
 * A block naming only one requirement still renders both cards: the missing one
 * reads as an unknown state ("Status unavailable"), never as `not_started`,
 * because inventing "nothing has happened" is a claim nobody made.
 */
export function readAdvanceRequirements(
  raw: unknown,
): ReadAdvanceRequirements | null {
  const block = record(raw);
  if (block === null) return null;
  const bank = readEvidence(block.bank);
  const accounting = readEvidence(block.accounting);
  if (bank === null && accounting === null) return null;
  const unknown: AdvanceEvidence = { state: null, via: null };
  return {
    bank: bank ?? unknown,
    accounting: accounting ?? unknown,
    satisfied: block.satisfied === true,
  };
}

const STATE_LABELS: Record<AdvanceEvidenceState, string> = {
  not_started: "Not started",
  link_sent: "Link sent",
  connected: "Connected",
  documents_received: "Documents received",
  satisfied: "Satisfied",
};

/** The card's chip. An unknown state says so, never a blank. */
export function advanceEvidenceStateLabel(
  state: AdvanceEvidenceState | null,
): string {
  return state === null ? "Status unavailable" : STATE_LABELS[state];
}

/** Whether the card still takes uploads and offers its link. A requirement
 *  already met, and one this build cannot read, offer nothing. */
export function isEvidenceOpen(evidence: AdvanceEvidence): boolean {
  return evidence.state !== null && evidence.state !== "satisfied";
}

/** What the section says once both requirements are met. */
export const ADVANCE_WITH_UNDERWRITER =
  "Your application is with your underwriter";

/** The opt-in offer's one sentence on the document alternative. */
export const ADVANCE_DOCUMENT_ALTERNATIVE =
  "Rather not connect a link? You can upload bank or financial documents on the insured’s account page instead.";

/** The card titles and their one-sentence document alternative. */
export const EVIDENCE_COPY: Record<
  AdvanceEvidenceRequirement,
  { title: string; alternative: string }
> = {
  bank: {
    title: "Bank",
    alternative:
      "No bank link? Upload a personal financial statement or bank statements instead.",
  },
  accounting: {
    title: "Accounting",
    alternative:
      "No accounting link? Upload financial statements (income statement, balance sheet, cash flow, audited year-end financials) instead.",
  },
};

// ── The accepted document types, per requirement ────────────────────────────

/** One accepted type for a requirement, as PMS serves it. `key` is an open
 *  string: these ids are ahead of the pinned `DocumentTypeKey` vocabulary. */
export interface RequirementDocumentType {
  key: string;
  label: string;
}

export interface RequirementDocumentTypes {
  types: RequirementDocumentType[];
  rules: DocumentRules;
}

/**
 * GET /api/broker/document-types?requirement=bank|accounting — the types this
 * requirement accepts, plus the platform file rules (PMS-S722, pinned).
 *
 * Rows without a string key and label are dropped rather than rendered blank.
 */
export async function getRequirementDocumentTypes(
  request: AuthedRequester,
  requirement: AdvanceEvidenceRequirement,
): Promise<RequirementDocumentTypes> {
  const body = await request<{ types?: unknown; rules: DocumentRules }>(
    `/api/broker/document-types?requirement=${encodeURIComponent(requirement)}`,
  );
  const rows: unknown[] = Array.isArray(body.types) ? body.types : [];
  const types = rows.flatMap((raw) => {
    const row = record(raw);
    return row && typeof row.key === "string" && typeof row.label === "string"
      ? [{ key: row.key, label: row.label }]
      : [];
  });
  return { types, rules: readRules(body.rules) };
}

// ── The account-addressed document writes (PMS-S722) ────────────────────────

const ACCOUNTS = "/api/broker/accounts";

/**
 * POST /api/broker/accounts/{id}/documents — file ONE document on the account
 * (multipart `files` + `type`). Answers 201 `{documents: [row]}` (PMS-S722,
 * pinned).
 *
 * Account-addressed because the evidence (and a capacity request's documents)
 * belongs to the insured, and these surfaces have no order to address —
 * picking one on the broker's behalf is ruled out (docs/bonds.md).
 * The same `DOCUMENT_REJECTED` envelope as the order route, read by
 * `documentRejection`.
 */
export async function uploadAccountDocument(
  upload: AuthedUploader,
  accountId: string,
  input: { file: File; type: string },
): Promise<AccountDocument> {
  const form = new FormData();
  form.append("files", input.file);
  form.append("type", input.type);
  const body = await upload<{ documents?: AccountDocument[] }>(
    `${ACCOUNTS}/${encodeURIComponent(accountId)}/documents`,
    form,
  );
  const [document] = Array.isArray(body.documents) ? body.documents : [];
  if (!document) {
    // No reference back is not a success this client can show (NN #7).
    throw new ApiError(
      201,
      "That file was accepted but no document reference came back — try uploading it again.",
    );
  }
  return document;
}

/**
 * DELETE /api/broker/accounts/{id}/documents/{docId} — 204, or 409 once the
 * application it belongs to is with the underwriter (PMS-S722, pinned).
 */
export function deleteAccountDocument(
  request: AuthedRequester,
  accountId: string,
  documentId: string,
): Promise<void> {
  return request<void>(
    `${ACCOUNTS}/${encodeURIComponent(accountId)}/documents/${encodeURIComponent(documentId)}`,
    { method: "DELETE" },
  );
}
